// キャンペーン進行のlocalStorage永続化。
// v1 (memoryHints) は廃止。 v2 は「dream-hound が index0」前提だったため、
// 一旦完成版 (2026-06-11・導入戦が index0 に入りボス列が変わった) で v3 へ。
// v3 は保存時のボス列 (rosterIds) を同梱し、 ロード時に現在の列と一致しなければ破棄する
// = 将来ボスを足し替えても古いセーブが壊れた位置を指さない。

// セーブ2層化 (2026-07-29・設計 = docs/candidates/2026-07-29-achievement-flavor-text-design.md §8):
//   save:v3   = 周回層。 ボス列の指紋つき・終幕で戻る。 このファイルの上半分。
//   record:v1 = 消えない層。 指紋を持たず、終幕でもボス追加でも消えない。 下半分 (loadRecord〜)。

import type { CampaignState, GameSettings } from './types'
import type { PersistentRecord, RunTotals } from './record'
import { createRecord, emptyTotals } from './record'
import { BOSS_DEFINITIONS } from './bosses'

const STORAGE_KEY = 'shinige-dreamland:save:v3'
const LEGACY_KEYS = ['shinige-dreamland:save:v2']
const SETTINGS_KEY = 'shinige-dreamland:settings:v1'
const RECORD_KEY = 'shinige-dreamland:record:v1'

// 設定の既定値 (音・揺れは全てON / 難易度は初演)。 createInitialState の初期値と一致させること。
//   2026-10-09 ユーザー裁定: 本番の難しさは初演。 千秋楽は終幕まで通した後でだけ選べる (isSenshurakuOpen)。
export const DEFAULT_SETTINGS: GameSettings = { bgmOn: true, seOn: true, shakeOn: true, difficulty: 'hatsuen' }

type SaveFile = {
  version: 3
  rosterIds: string[]
  campaign: CampaignState
}

function currentRosterIds(): string[] {
  return BOSS_DEFINITIONS.map((b) => b.id)
}

// JSON は型宣言を保証しない。ゲームへ渡す前に入れ子まで検査する。
function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isNonNegativeNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
}

function isCount(value: unknown): value is number {
  return isNonNegativeNumber(value) && Number.isInteger(value)
}

function isStrings(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(item => typeof item === 'string')
}

function isDictionary<T>(value: unknown, valid: (item: unknown) => item is T): value is Record<string, T> {
  return isObject(value) && Object.values(value).every(valid)
}

function isBossChoice(value: unknown): value is 'true' | 'loop' {
  return value === 'true' || value === 'loop'
}

function restoreCampaign(value: unknown): CampaignState | null {
  if (!isObject(value)) return null
  const c = value
  // 後追加の項目だけ従来の欠損互換を保つ。必須の進行・戦績を推測で作り直さない。
  const timeByBossId = c.timeByBossId ?? {}
  const totalTime = c.totalTime ?? 0
  const awakening = c.awakening ?? 0
  const seenScenes = c.seenScenes ?? []
  const choicesByBossId = c.choicesByBossId ?? {}
  const justParryByBossId = c.justParryByBossId ?? {}
  const guardByBossId = c.guardByBossId ?? {}
  if (!isCount(c.currentBossIndex) || c.currentBossIndex >= BOSS_DEFINITIONS.length
    || !isStrings(c.defeatedBossIds)
    || !isDictionary(c.seenAttacksByBossId, isStrings)
    || !isDictionary(c.deathsByBossId, isCount)
    || !isDictionary(c.parrySuccessByBossId, isCount)
    || !isDictionary(c.parryFailByBossId, isCount)
    || !isDictionary(c.dodgeByBossId, isCount)
    || !isDictionary(c.counterByBossId, isCount)
    || !isCount(c.totalDeaths)
    || !isDictionary(timeByBossId, isNonNegativeNumber)
    || !isNonNegativeNumber(totalTime)
    || !isNonNegativeNumber(awakening)
    || !isStrings(seenScenes)
    || !isDictionary(choicesByBossId, isBossChoice)
    || !isDictionary(justParryByBossId, isCount)
    || !isDictionary(guardByBossId, isCount)) return null

  // 検査済みの項目だけ復元する。scratch と廃止済み revealedLoreByBossId は保存に戻さない。
  // 未登録のボス・攻撃・シーンIDは記録として保持する (現役IDでふるい落とさない)。
  return {
    currentBossIndex: c.currentBossIndex,
    defeatedBossIds: c.defeatedBossIds,
    deathsByBossId: c.deathsByBossId,
    seenAttacksByBossId: c.seenAttacksByBossId,
    parrySuccessByBossId: c.parrySuccessByBossId,
    parryFailByBossId: c.parryFailByBossId,
    dodgeByBossId: c.dodgeByBossId,
    counterByBossId: c.counterByBossId,
    totalDeaths: c.totalDeaths,
    timeByBossId, totalTime, awakening, seenScenes, choicesByBossId,
    justParryByBossId, guardByBossId,
  }
}

export function loadCampaign(): CampaignState | null {
  try {
    if (typeof localStorage === 'undefined') return null
    // 旧版セーブは読まずに掃除 (ボス列前提が違うため安全に破棄)
    for (const k of LEGACY_KEYS) localStorage.removeItem(k)
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const data: unknown = JSON.parse(raw)
    if (!isObject(data) || data.version !== 3) return null
    const roster = currentRosterIds()
    if (!Array.isArray(data.rosterIds)
      || data.rosterIds.length !== roster.length
      || data.rosterIds.some((id, i) => id !== roster[i])) {
      return null
    }
    return restoreCampaign(data.campaign)
  } catch {
    return null
  }
}

export function saveCampaign(c: CampaignState): void {
  // 下書き (稽古・dev フック) の進行は本物のセーブへ書かない (2026-07-26 稽古セーブ破壊修正)。
  //   進行データは全て { ...campaign, ... } の形で更新されるため、 この印は自動的に受け継がれる=
  //   保存箇所が将来増えてもここ1箇所で守られる。
  if (c.scratch) return
  try {
    if (typeof localStorage === 'undefined') return
    const file: SaveFile = { version: 3, rosterIds: currentRosterIds(), campaign: c }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(file))
  } catch {}
}

// ⚠️ 周回層だけを消す。 消えない層 (record:v1) には触らない。
//   終幕の完走でここを呼ぶ前に、必ず foldRunIntoRecord + saveRecord を済ませること
//   (でないと、その周の記録が畳まれないまま消える)。 対になっている場所 = game.ts の終幕完走。
export function resetCampaign(): void {
  try {
    if (typeof localStorage === 'undefined') return
    localStorage.removeItem(STORAGE_KEY)
    for (const k of LEGACY_KEYS) localStorage.removeItem(k)
  } catch {}
}

// 何もかも消す。 DEV の T キー (進捗リセット) 専用 = 配布版のプレイヤーには経路が無い
//   (input.ts の KeyT は import.meta.env.DEV の中)。
export function resetEverything(): void {
  resetCampaign()
  try {
    if (typeof localStorage === 'undefined') return
    localStorage.removeItem(RECORD_KEY)
  } catch {}
}

// ===== 消えない層 (record:v1) =====
// 壊れていたら空の記録へ倒す (読めないより、空から数え直すほうがマシ)。
//   ⚠️ ここでボス列 (rosterIds) を見ないのが要点。 ボスを足しても並べ替えても、この層は生き残る。

function sanitizeTotals(v: unknown): RunTotals {
  const t = isObject(v) ? v : {}
  const num = (x: unknown): number => (typeof x === 'number' && Number.isFinite(x) && x >= 0 ? x : 0)
  return {
    deaths: num(t.deaths), timeSec: num(t.timeSec),
    parrySuccess: num(t.parrySuccess), parryFail: num(t.parryFail),
    dodge: num(t.dodge), counter: num(t.counter),
    // 2026-07-30 追加。 これ以前に保存された record:v1 には無いので 0 で埋まる。
    justParry: num(t.justParry), guard: num(t.guard),
  }
}

const ENDING_KINDS = ['stay', 'loop', 'loop2', 'true'] as const

export function loadRecord(): PersistentRecord {
  try {
    if (typeof localStorage === 'undefined') return createRecord()
    const raw = localStorage.getItem(RECORD_KEY)
    if (!raw) return createRecord()
    const d: unknown = JSON.parse(raw)
    if (!isObject(d) || d.version !== 1) return createRecord()
    const strs = (v: unknown): string[] => (Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : [])
    // Object.fromEntries は __proto__ 等も通常の自分のキーとして保持する。
    const carriedByBossId = Object.fromEntries(
      Object.entries(isObject(d.carriedByBossId) ? d.carriedByBossId : {})
        .map(([id, totals]) => [id, sanitizeTotals(totals)]),
    ) // 未知の bossId は残す (将来戻す可能性。表示は既知idに限定)
    return {
      version: 1,
      runsCompleted: isNonNegativeNumber(d.runsCompleted) ? Math.floor(d.runsCompleted) : 0,
      endingsSeen: strs(d.endingsSeen).filter((s): s is PersistentRecord['endingsSeen'][number] =>
        (ENDING_KINDS as readonly string[]).includes(s)),
      clearedBossIdsEver: strs(d.clearedBossIdsEver),
      carried: d.carried ? sanitizeTotals(d.carried) : emptyTotals(),
      carriedByBossId,
      achievements: strs(d.achievements),
    }
  } catch {
    return createRecord()
  }
}

export function saveRecord(rec: PersistentRecord): void {
  try {
    if (typeof localStorage === 'undefined') return
    localStorage.setItem(RECORD_KEY, JSON.stringify(rec))
  } catch {}
}

// ===== 千秋楽の入口 (2026-10-09 ユーザー裁定「高難度は一度クリアしてから」) =====
// 「物語を終幕まで通した」印 = 消えない層 (record:v1) の runsCompleted / endingsSeen。
//   終幕の ADV を見終えて進行入力を押した時に、 game.ts の終幕完走が foldRunIntoRecord + markEndingSeen で立てる
//   (周回層を消しても残る・稽古と一戦だけモードは下書き印つきで畳まれないので立たない)。 新しい印は足していない。
//   このセーブ層ができた 2026-07-29 より前に終幕まで通した人は印が無いので、 もう一度終幕まで通すと開く。
let senshurakuGateOverride: boolean | null = null
// DEV 専用 (main.ts の DEV の箱から ?clearMark=on|off と __setClearMark で呼ぶ): 保存を触らず、 印の有無だけを決め打ちする。
//   配布版は呼ばない = いつも記録を見る。
export function setSenshurakuGateOverride(v: boolean | null): void {
  senshurakuGateOverride = v
}
export function isSenshurakuOpen(): boolean {
  if (senshurakuGateOverride !== null) return senshurakuGateOverride
  const rec = loadRecord()
  return rec.runsCompleted >= 1 || rec.endingsSeen.length > 0
}

// 設定 (音・画面揺れ) の永続化。 キャンペーンセーブとは別キーで持つ =
// セーブが無い初見プレイヤーでも音量設定だけは保持される。
// 破損・未保存・型不一致はすべて既定値へフォールバックする (fail-safe)。
export function loadSettings(): GameSettings {
  try {
    if (typeof localStorage === 'undefined') return { ...DEFAULT_SETTINGS }
    const raw = localStorage.getItem(SETTINGS_KEY)
    if (!raw) return { ...DEFAULT_SETTINGS }
    const data = JSON.parse(raw) as Partial<GameSettings>
    return {
      bgmOn: typeof data.bgmOn === 'boolean' ? data.bgmOn : DEFAULT_SETTINGS.bgmOn,
      seOn: typeof data.seOn === 'boolean' ? data.seOn : DEFAULT_SETTINGS.seOn,
      shakeOn: typeof data.shakeOn === 'boolean' ? data.shakeOn : DEFAULT_SETTINGS.shakeOn,
      // 旧セーブ (difficulty 無し) や不正値は既定 (初演) へフォールバック。
      //   千秋楽が保存されていても、 終幕まで通した印が無ければ初演へ戻す (2026-10-09)。
      difficulty: data.difficulty === 'senshuraku' && isSenshurakuOpen() ? 'senshuraku'
        : data.difficulty === 'hatsuen' ? 'hatsuen' : DEFAULT_SETTINGS.difficulty,
    }
  } catch {
    return { ...DEFAULT_SETTINGS }
  }
}

export function saveSettings(s: GameSettings): void {
  try {
    if (typeof localStorage === 'undefined') return
    const file: GameSettings = { bgmOn: s.bgmOn, seOn: s.seOn, shakeOn: s.shakeOn, difficulty: s.difficulty }
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(file))
  } catch {}
}
